import { supabase } from '@/integrations/supabase/client';
import { addCoins } from './pointsService';

export interface FriendInvitation {
    id: string;
    inviter_id: string;
    invited_email: string;
    is_registered: boolean;
    created_at: string;
    registered_at?: string;
}

const MAX_INVITATIONS = 5;
const POINTS_PER_INVITATION = 20;

/**
 * Get all friend invitations for a player
 */
export async function getFriendInvitations(playerId: string): Promise<FriendInvitation[]> {
    const { data, error } = await supabase
        .from('friends_invitations' as any)
        .select('*')
        .eq('inviter_id', playerId)
        .order('created_at', { ascending: false });

    if (error) {
        console.error('Error fetching friend invitations:', error);
        return [];
    }

    return (data as any[]) || [];
}

/**
 * Get invitation progress (validated/total)
 */
export async function getInvitationProgress(playerId: string): Promise<{ validated: number; total: number; remaining: number }> {
    const invitations = await getFriendInvitations(playerId);
    const validated = invitations.filter(inv => inv.is_registered).length;
    const total = invitations.length;
    const remaining = Math.max(0, MAX_INVITATIONS - total);

    return { validated, total, remaining };
}

/**
 * Send a friend invitation by email
 */
export async function sendFriendInvitation(playerId: string, email: string): Promise<{ success: boolean; error?: string }> {
    try {
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return { success: false, error: 'Invalid email format' };
        }

        // Normalize email to lowercase for comparison
        const normalizedEmail = email.toLowerCase().trim();

        // Check if player has reached invitation limit
        const invitations = await getFriendInvitations(playerId);
        if (invitations.length >= MAX_INVITATIONS) {
            return { success: false, error: `You can only invite up to ${MAX_INVITATIONS} friends` };
        }

        // Check if invitation already exists
        const { data: existingInvitation, error: inviteCheckError } = await supabase
            .from('friends_invitations' as any)
            .select('id')
            .eq('inviter_id', playerId)
            .ilike('invited_email', normalizedEmail)
            .maybeSingle();

        if (inviteCheckError) {
            console.error('Error checking existing invitation:', inviteCheckError);
        }

        if (existingInvitation) {
            return { success: false, error: 'You have already invited this friend' };
        }

        // Check if the email is the player's own email
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.email && user.email.toLowerCase() === normalizedEmail) {
            return { success: false, error: 'You cannot invite yourself' };
        }

        // Insert invitation
        const { data, error } = await supabase
            .from('friends_invitations' as any)
            .insert({
                inviter_id: playerId,
                invited_email: normalizedEmail
            } as any)
            .select()
            .single();

        if (error) {
            console.error('Error saving invitation:', error);
            return { success: false, error: 'Failed to send invitation' };
        }

        // Send invitation email via Brevo
        try {
            const inviterName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'A friend';
            const inviterEmail = user?.email || '';

            const emailResponse = await supabase.functions.invoke('send-invitation-email', {
                body: {
                    inviterName,
                    inviterEmail,
                    invitedEmail: normalizedEmail
                }
            });

            // Check if there's an HTTP error (non-2xx status)
            if (emailResponse.error) {
                // Delete the invitation since it was rejected
                const { error: deleteError } = await supabase
                    .from('friends_invitations' as any)
                    .delete()
                    .eq('id', (data as any).id);

                if (deleteError) {
                    console.error('Failed to delete invitation:', deleteError);
                }

                // Return user-friendly error message
                return { success: false, error: 'This user is already registered on IndieKeys' };
            }

            // Check if the response data contains an error
            if (emailResponse.data?.error) {
                // Delete the invitation
                const { error: deleteError } = await supabase
                    .from('friends_invitations' as any)
                    .delete()
                    .eq('id', (data as any).id);

                if (deleteError) {
                    console.error('Failed to delete invitation:', deleteError);
                }

                return { success: false, error: emailResponse.data.error };
            }
        } catch (emailError) {
            console.error('Error invoking email function:', emailError);
            // Continue even if email fails - invitation is saved
        }

        return { success: true };
    } catch (error) {
        console.error('Error in sendFriendInvitation:', error);
        return { success: false, error: 'An unexpected error occurred' };
    }
}

/**
 * Validate a friend invitation when a user signs up
 * This is called by the database trigger, but can also be called manually
 */
export async function validateFriendInvitation(email: string): Promise<boolean> {
    try {
        const normalizedEmail = email.toLowerCase().trim();

        // Find pending invitation
        const { data: invitation, error: invitationError } = await supabase
            .from('friends_invitations' as any)
            .select('*')
            .eq('invited_email', normalizedEmail)
            .eq('is_registered', false)
            .single();

        if (invitationError || !invitation) {
            return false; // No pending invitation found
        }

        // Update invitation status
        const { error: updateError } = await supabase
            .from('friends_invitations' as any)
            .update({
                is_registered: true,
                registered_at: new Date().toISOString()
            } as any)
            .eq('id', (invitation as any).id);

        if (updateError) {
            console.error('Error updating invitation:', updateError);
            return false;
        }

        // Get the invite_friend mission
        const { data: mission, error: missionError } = await supabase
            .from('missions' as any)
            .select('*')
            .eq('name', 'invite_friend')
            .eq('is_active', true)
            .single();

        if (missionError || !mission) {
            console.error('Mission not found:', missionError);
            return true; // Invitation validated but mission not found
        }

        // Add mission completion record
        const { error: missionCompletionError } = await supabase
            .from('player_missions' as any)
            .insert({
                player_id: (invitation as any).inviter_id,
                mission_id: (mission as any).id,
                points_earned: (mission as any).points_reward,
                completion_date: new Date().toISOString().split('T')[0]
            } as any);

        if (missionCompletionError) {
            console.error('Error completing mission:', missionCompletionError);
        }

        // Add coins to inviter
        await addCoins((invitation as any).inviter_id, POINTS_PER_INVITATION);

        return true;
    } catch (error) {
        console.error('Error validating friend invitation:', error);
        return false;
    }
}
